import { Form } from "@unform/web";
import { deleteObject, getStorage, ref } from "firebase/storage";
import {
  ArrowUpDown,
  BookMarked,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Contact,
  Download,
  ExternalLink,
  FileText,
  Files,
  FolderOpen,
  GraduationCap,
  Loader2,
  MessageSquare,
  NotebookPen,
  Pencil,
  Send,
  Trash,
  X,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { isVisibleStudentGroup } from "~/functions/studentGroupStatus";
import { Zoom, toast } from "react-toastify";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import {
  countries_list,
  getRegistries,
  getTabsPermissions,
  handleUpdatedFields,
  tabAllowed,
} from "~/functions";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import DatePicker from "~/components/RegisterForm/DatePicker";
import { format, parseISO, isValid } from "date-fns";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { useSelector } from "react-redux";
import { FullGridContext } from "../..";
import {
  genderOptions,
  inactivateReasonsDescriptions,
  inactiveReasonsOptions,
  optionsCategory,
  optionsContactForm,
  optionsStatus,
  studentTypeOfDocuments,
} from "~/functions/selectPopoverOptions";
import { app } from "~/services/firebase";
import PricesSimulation from "~/components/PricesSimulation";
import PhoneNumberInput from "~/components/RegisterForm/PhoneNumberInput";
import FindGeneric from "~/components/Finds/FindGeneric";
import Comments from "~/pages/Financial/Inbounds/Recurrence/Preview/Comments";
import { Scope } from "@unform/core";
import FileInput from "~/components/RegisterForm/FileInput";
import uploadFile, { organizeMultiAndSingleFiles } from "~/functions/uploadFile";
import PDFViewer from "~/components/PDFViewer";
import Textarea from "~/components/RegisterForm/Textarea";
import FileInputMultiple from "~/components/RegisterForm/FileInputMultiple";
import { AlertContext } from "~/App";
import { saveAs } from "file-saver";

export const InputContext = createContext({});

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);

  const tabsPermissions = getTabsPermissions("students", FullGridContext);
  const [openedPdf, setOpenedPDF] = useState(null);
  const [openedPdfSection, setOpenedPdfSection] = useState(null);
  const [filteredDocs, setFilteredDocs] = useState(studentTypeOfDocuments[0]);
  const [loading, setLoading] = useState(false);
  const [groupPdfLoading, setGroupPdfLoading] = useState({});
  const [academicHistoryTab, setAcademicHistoryTab] = useState("group-history");
  const [groupHistorySort, setGroupHistorySort] = useState({
    column: null,
    direction: "asc",
  });

  const [pageData, setPageData] = useState({
    name: "",
    last_name: "",
    email: "",
    preferred_contact_form: "",
    visa_expiration: null,
    birth_country: "",
    birth_state: "",
    birth_city: "",
    gender: null,
    date_of_birth: null,
    passport_number: "",
    visa_number: "",
    nsevis: "",
    whatsapp: "",
    whatsapp_ddi: "",
    home_country_phone_ddi: "",
    home_country_phone: "",
    address: "",
    loaded: false,
    discount_id: null,
    discount: null,
    total_tuition: 0,
    total_discount: 0,
    registration_fee: 0,
    books: 0,
    tuition_original_price: 0,
    tuition_in_advance: false,
    inactive_reason: "",
    searchFields: {
      filial_id: null,
      processtype_id: null,
      processsubstatus_id: null,
    },
    classroom: null,
    studentgroup: null,
    programs: [],
    docs: [],
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("student-information");
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [altProgramStartDate, setAltProgramStartDate] = useState("");
  const [altProgramEndDate, setAltProgramEndDate] = useState("");
  const [altProgramMemo, setAltProgramMemo] = useState("");
  const [altProgramMode, setAltProgramMode] = useState(null);
  const [altProgramFileDeleted, setAltProgramFileDeleted] = useState(false);
  const [programChangeLogs, setProgramChangeLogs] = useState([]);
  const [loadingProgramChangeLogs, setLoadingProgramChangeLogs] = useState(false);
  const [pendingDeleteDocs, setPendingDeleteDocs] = useState([]);
  const [studentHistory, setStudentHistory] = useState([]);
  const [loadingStudentHistory, setLoadingStudentHistory] = useState(false);
  const [historyEventDateFrom, setHistoryEventDateFrom] = useState("");
  const [historyEventDateTo, setHistoryEventDateTo] = useState("");
  const [historyActionFilter, setHistoryActionFilter] = useState("");
  const [admissionSubTab, setAdmissionSubTab] = useState("admission");
  const [enrollmentHistory, setEnrollmentHistory] = useState([]);
  const [loadingEnrollmentHistory, setLoadingEnrollmentHistory] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [downloadingEnrollmentPDF, setDownloadingEnrollmentPDF] =
    useState(null);
  const [sendingI20, setSendingI20] = useState(null);
  const subnameResolveRef = useRef(null);
  const generalForm = useRef();
  const resolveIssueRef = useRef(false);
  const auth = useSelector((state) => state.auth);
  const { alertBox } = useContext(AlertContext);

  const studentHistoryActions = useMemo(
    () =>
      [...new Set(studentHistory.map((row) => row.action).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b)),
    [studentHistory]
  );

  const filteredStudentHistory = useMemo(
    () =>
      studentHistory.filter((row) => {
        const eventDate = String(row.created_at || "").slice(0, 10);
        const matchesEventDateFrom =
          !historyEventDateFrom ||
          (Boolean(eventDate) && eventDate >= historyEventDateFrom);
        const matchesEventDateTo =
          !historyEventDateTo ||
          (Boolean(eventDate) && eventDate <= historyEventDateTo);
        const matchesAction =
          !historyActionFilter || row.action === historyActionFilter;
        return matchesEventDateFrom && matchesEventDateTo && matchesAction;
      }),
    [
      studentHistory,
      historyEventDateFrom,
      historyEventDateTo,
      historyActionFilter,
    ]
  );

  function askShowSubname() {
    return new Promise((resolve) => {
      subnameResolveRef.current = resolve;
      alertBox({
        title: "Document Names",
        description: "Do you want to keep the original file names or generate new names for the documents?",
        buttons: [
          {
            title: "Keep original names",
            onPress: () => {
              subnameResolveRef.current(false);
              subnameResolveRef.current = null;
            },
          },
          {
            title: "I want new names",
            onPress: () => {
              subnameResolveRef.current(true);
              subnameResolveRef.current = null;
            },
          },
        ],
      });
    });
  }

  useEffect(() => {
    document.getElementById("scrollPage").scrollTo(0, 0);
  }, [activeMenu]);

  useEffect(() => {
    if (activeMenu === "student-admission" && id !== "new" && enrollmentHistory.length === 0) {
      setLoadingEnrollmentHistory(true);
      api.get(`/students/${id}/get-enrollment-history-bystudent`)
        .then(({ data }) => setEnrollmentHistory(data || []))
        .catch(() => setEnrollmentHistory([]))
        .finally(() => setLoadingEnrollmentHistory(false));
    }
  }, [activeMenu]);

  function isEnrollmentProcess(enrollment) {
    return (enrollment?.application || "")
      .toLowerCase()
      .includes("enrollment process");
  }

  function handleEnrollmentPDF(enrollment) {
    const isGlobal =
      enrollment?.contract?.processtypes?.name === "Non-F1" &&
      enrollment?.contract?.processsubstatuses?.name === "Global";
    const endpoint = isGlobal
      ? `/pdf/new-enrollment-global/${enrollment.id}`
      : `/pdf/new-enrollment/${enrollment.id}`;
    setDownloadingEnrollmentPDF(enrollment.id);
    api
      .get(endpoint, { responseType: "blob" })
      .then((res) => {
        const pdfBlob = new Blob([res.data], { type: "application/pdf" });
        saveAs(pdfBlob, `enrollment_${enrollment.id}_new.pdf`);
      })
      .catch(() => {
        toast("Could not download the enrollment PDF.", {
          type: "error",
          autoClose: 3000,
        });
      })
      .finally(() => setDownloadingEnrollmentPDF(null));
  }

  function handleSendI20(program) {
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Do you want to send this I-20 by e-mail to the student?",
      buttons: [
        {
          title: "No",
          class: "cancel",
          onPress: () => null,
        },
        {
          title: "Yes",
          onPress: async () => {
            setSendingI20(program.id);
            try {
              const { data } = await api.post(`/send-i20/${pageData.id}`, {
                student_program_id: program.id,
              });
              toast(data?.message || "I-20 e-mail sent.", {
                autoClose: 3000,
              });
            } catch (err) {
              toast(err?.response?.data?.error || "Could not send the I-20.", {
                type: "error",
                autoClose: 3000,
              });
            } finally {
              setSendingI20(null);
            }
          },
        },
      ],
    });
  }

  const countriesOptions = countries_list.map((country) => {
    return { value: country, label: country };
  });
  async function getPageData() {
    if (id !== "new") {
      try {
        const { data } = await api.get(`/students/${id}`);
        const { data: docs } = await api.get(`/student-documents/${id}`);
        const { data: defaultOtherDocs } = await api.get(
          `/documentsByOrigin?origin=Student`
        );
        setPageData({
          ...data,
          searchFields: {
            processtype_id: data.processtype_id,
            processsubstatus_id: data.processsubstatus_id,
            filial_id: data.filial_id,
          },
          docs,
          defaultOtherDocs,
          loaded: true,
        });
        const {
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        } = data;
        const registries = await getRegistries({
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        });
        setRegistry(registries);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    } else {
      setPageData({ ...pageData, loaded: true });
      setFormType("full");
    }
  }

  useEffect(() => {
    getPageData();
  }, []);

  useEffect(() => {
    const program = pageData?.programs?.[0];
    if (!program) return;
    if (!altProgramStartDate && program.start_date) {
      setAltProgramStartDate(program.start_date);
    }
    if (!altProgramEndDate && program.end_date) {
      setAltProgramEndDate(program.end_date);
    }
  }, [pageData?.programs?.[0]?.id]);

  const fetchProgramChangeLogs = async (studentId) => {
    if (!studentId) return;
    setLoadingProgramChangeLogs(true);
    try {
      const { data } = await api.get(
        `/students/${studentId}/studentprogram-change-logs`
      );
      setProgramChangeLogs(data?.rows || []);
    } catch {
      setProgramChangeLogs([]);
    } finally {
      setLoadingProgramChangeLogs(false);
    }
  };

  useEffect(() => {
    if (!id || id === "new" || activeMenu !== "student-dso") return;
    fetchProgramChangeLogs(id);
  }, [activeMenu, id]);

  useEffect(() => {
    if (!id || id === "new" || activeMenu !== "student-history") return;
    async function fetchStudentHistory() {
      setLoadingStudentHistory(true);
      try {
        const { data } = await api.get(`/students/${id}/history`);
        setStudentHistory(data);
      } catch {
        setStudentHistory([]);
      } finally {
        setLoadingStudentHistory(false);
      }
    }
    fetchStudentHistory();
  }, [activeMenu, id]);

  useEffect(() => {
    setHistoryEventDateFrom("");
    setHistoryEventDateTo("");
    setHistoryActionFilter("");
  }, [id]);

  async function handleChangeProgram() {
    if (!altProgramMode) return;
    if (!altProgramMemo || altProgramMemo.trim() === "") {
      toast("Comment is required.", { type: "error", autoClose: 2000 });
      return;
    }
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Do you want to update the expected start date for this student?",
      buttons: [
        {
          title: "No",
          class: "cancel",
          onPress: async () => {
            await executeChangeProgram(false);
          },
        },
        {
          title: "Yes",
          onPress: async () => {
            await executeChangeProgram(true);
          },
        },
      ],
    });
  }

  async function executeChangeProgram(updateExpectedStartDate) {
    const programId = pageData?.programs?.[0]?.id;
    const studentId = id;
    const hasProgram = !!programId;
    setLoading(true);
    try {
      const fileField = generalForm?.current?.getFieldValue("alt_program_file");
      let filePayload = null;
      if (fileField) {
        if (hasProgram) {
          const [result] = await Promise.all(
            organizeMultiAndSingleFiles(
              [{ file_id: fileField, document_id: null }],
              "StudentPrograms"
            )
          );
          const resolved = Array.isArray(result) ? result[0] : result;
          if (resolved) {
            const { document_id, ...rest } = resolved;
            filePayload = rest;
          }
        } else {
          filePayload = await uploadFile(fileField, "StudentPrograms");
        }
      }

      if (!hasProgram) {
        await api.post(`/studentprogram`, {
          student_id: studentId,
          start_date: altProgramStartDate || null,
          end_date: altProgramEndDate || null,
          type: "first-program",
          memo: altProgramMemo || "Initial program",
          file_id: filePayload,
        });
      } else {
        const payload = {
          start_date: altProgramStartDate || null,
          end_date: altProgramEndDate || null,
          file_id: filePayload,
          memo: altProgramMemo,
          type: altProgramMode,
          update_expected_start_date: updateExpectedStartDate,
        };
        if (altProgramMode === "add-new") {
          payload.student_id = studentId || null;
        }
        await api.post(`/studentprogram/${programId}/change-program`, payload);
      }

      await Promise.all([
        fetchProgramChangeLogs(studentId),
        getPageData(),
      ]);
      setAltProgramMode(null);
      setAltProgramMemo("");
      setAltProgramFileDeleted(false);
      setAltProgramStartDate("");
      setAltProgramEndDate("");
      if (generalForm?.current?.setFieldValue) {
        generalForm.current.setFieldValue("alt_program_file", null);
      }
      toast("Program updated.", { type: "success", autoClose: 2000 });
    } catch (err) {
      const message = err?.response?.data?.error || "Failed to update program.";
      toast(message, { type: "error", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }

  async function handleGeneralFormSubmit(data) {
    const isResolveIssue = resolveIssueRef.current;
    resolveIssueRef.current = false;

    if (successfullyUpdated && !isResolveIssue) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }

    if (pendingDeleteDocs.length > 0) {
      for (const document_id of pendingDeleteDocs) {
        try {
          await api.delete(`/studentdocuments/${document_id}`);
        } catch (err) {
          toast(err?.response?.data?.error || "Failed to delete file.", { type: "error", autoClose: 3000 });
        }
      }
      setPendingDeleteDocs([]);
    }

    if (data.email && (data.email !== data.email.toLowerCase() || data.email !== data.email.trim())) {
      toast("Email must be lowercase and have no leading/trailing spaces.", {
        type: "warning",
        autoClose: 4000,
      });
      return;
    }

    console.log("[other_docs raw]", data.other_docs);
    console.log("[other_docs file_ids]", data.other_docs?.map((doc) => ({ file_id: doc.file_id, type: typeof doc.file_id })));

    const hasNewFiles = data.other_docs?.some((doc) => doc.file_id && doc.file_id.length > 0);
    console.log("[hasNewFiles]", hasNewFiles);

    if (hasNewFiles) {
      const showSubname = await askShowSubname();

      const allPromises = organizeMultiAndSingleFiles(
        data.other_docs,
        "StudentDocuments"
      );
      Promise.all(allPromises).then(async (resolvedFiles) => {
        const flatFiles = resolvedFiles.flat().filter(Boolean);
        for (const file of flatFiles) {
          try {
            await api.post(`/studentdocuments`, {
              student_id: id,
              files: [{ ...file, show_subname: showSubname }],
            });
          } catch (err) {
            if (err?.response?.data?.error) {
              toast(err.response.data.error, { type: "error", autoClose: 3000 });
            }
            const storage = getStorage(app);
            const local = "StudentDocuments/" + file.key;
            const imageRef = ref(storage, local);
            deleteObject(imageRef);
          }
        }
      });
    }

    if (data.new_program && data.new_program.file_id) {
      const allPromises = organizeMultiAndSingleFiles(
        [data.new_program],
        "StudentPrograms"
      );
      let toastId = toast.loading("Files are being uploaded...");
      Promise.all(allPromises).then(async (files) => {
        files.map(async (file) => {
          if (!file) {
            return;
          }
          try {
            const { data: newprogram } = await api.post(`/studentprogram`, {
              student_id: id,
              file_id: file,
              start_date: data.new_program.start_date,
              end_date: data.new_program.end_date,
            });
            if (toastId) {
              toast.update(toastId, {
                render: "All files have been uploaded!",
                type: "success",
                autoClose: 3000,
                isLoading: false,
              });
            }
            setPageData({
              ...pageData,
              programs: [...pageData.programs, newprogram],
              start_date: pageData.start_date
                ? pageData.start_date
                : newprogram.start_date,
            });
            generalForm.current.setFieldValue("new_program.file_id", null);
            generalForm.current.setFieldValue("new_program.start_date", null);
            generalForm.current.setFieldValue("new_program.end_date", null);
          } catch (err) {
            if (err?.response?.data?.error) {
              toast.update(toastId, {
                render: err.response.data.error,
                type: "error",
                autoClose: 3000,
                isLoading: false,
              });
            }
            const storage = getStorage(app);
            const local = "StudentPrograms/" + file.key;
            const imageRef = ref(storage, local);
            deleteObject(imageRef);
          }
        });
      });
    } else {
      delete data.new_program;
      if (id === "new") {
        try {
          const { date_of_birth, visa_expiration } = data;
          delete data.id;
          const response = await api.post(`/students`, {
            ...data,
            date_of_birth: date_of_birth
              ? format(date_of_birth, "yyyy-MM-dd")
              : null,
            visa_expiration: visa_expiration
              ? format(visa_expiration, "yyyy-MM-dd")
              : null,
          });
          setOpened(response.data.id);
          setPageData({ ...pageData, ...data });
          setSuccessfullyUpdated(true);
          toast("Saved!", { autoClose: 1000 });
          handleOpened(null);
        } catch (err) {
          if (err?.response?.data?.error) {
            toast(err.response.data.error, { type: "error", autoClose: 3000 });
          }
        }
      } else if (id !== "new") {
        const updated = handleUpdatedFields(data, pageData);

        if (updated.length > 0 || isResolveIssue) {
          const objUpdated = Object.fromEntries(updated);
          const { date_of_birth, visa_expiration } = objUpdated;
          try {
            delete data.enrollment_date;
            delete data.expected_start_date;
            delete data.placement_test_location;
            delete data.placement_test_score;
            delete data.registration_number;
            delete data.start_date;
            delete data.inactivation_date;
            delete data.undefined;
            const formattedDateOfBirth = date_of_birth
              ? format(date_of_birth, "yyyy-MM-dd")
              : null;
            const formattedVisaExpiration = visa_expiration
              ? format(visa_expiration, "yyyy-MM-dd")
              : null;
            const { data: updatedStudent } = await api.put(`/students/${id}`, {
              ...data,
              date_of_birth: formattedDateOfBirth,
              visa_expiration: formattedVisaExpiration,
              ...(isResolveIssue ? { clear_overdue_invoice_flag: true } : {}),
            });
            setPageData({
              ...pageData,
              ...objUpdated,
              date_of_birth: formattedDateOfBirth,
              visa_expiration: formattedVisaExpiration,
              has_overdue_invoice: updatedStudent?.has_overdue_invoice,
              overdue_invoice_flagged_at:
                updatedStudent?.overdue_invoice_flagged_at,
            });
            setSuccessfullyUpdated(true);
            toast("Saved!", { autoClose: 1000 });
            if (!isResolveIssue) {
              handleOpened(null);
            }
          } catch (err) {
            if (err?.response?.data?.error) {
              toast(err.response.data.error, {
                type: "error",
                autoClose: 3000,
              });
            }
          }
        } else {
          // console.log(updated)
        }
      }
    }
  }

  function handleResolveOverdueInvoiceIssue() {
    resolveIssueRef.current = true;
    generalForm.current.submitForm();
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    setPendingDeleteDocs([]);
    handleOpened(null);
  }

  async function handleInactivate() {
    try {
      await api.delete(`students/${id}`);
      toast("Group Inactivated!", { autoClose: 1000 });
      handleOpened(null);
    } catch (err) {
      if (err?.response?.data?.error) {
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    }
  }

  async function handleDeleteDocument(document_id) {
    alertBox({
      title: "Attention!",
      descriptionHTML: `<p>Are you sure you want to delete this file?</p>`,
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: () => {
            setPendingDeleteDocs((prev) => [...prev, document_id]);
            setPageData((prev) => ({
              ...prev,
              docs: {
                ...prev.docs,
                other_docs: prev.docs?.other_docs?.filter((d) => d.id !== document_id),
              },
            }));
            setSuccessfullyUpdated(false);
            toast("File marked for deletion. Save Changes to confirm.", { autoClose: 2000, type: "info" });
          },
        },
      ],
    });
  }

  const StudentDocument = ({ doc, deletable = false, section }) => {
    return (
      <div className="flex flex-col items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => { setOpenedPDF(doc.url); setOpenedPdfSection(section); }}
          className="bg-slate-100 w-36 min-h-36 flex flex-col items-center justify-center gap-2 border rounded-md p-2 text-center text-xs text-slate-500 hover:border-primary hover:text-primary cursor-pointer"
        >
          <FileText size={32} strokeWidth={1.5} />
          <h3 className="w-full font-bold truncate" title={doc.name}>
            {doc.name?.length > 26
              ? doc.name.slice(0, 22) + "..." + (doc.name.includes(".") ? doc.name.slice(doc.name.lastIndexOf(".")) : "")
              : doc.name}
          </h3>
          <p>{(doc.size / 1024).toFixed(2)}KB</p>
        </button>
        {deletable && (
          <button
            type="button"
            onClick={() => handleDeleteDocument(doc.id)}
            className="text-red-500 cursor-pointer rounded-md py-2 px-6 flex flex-row items-center justify-center text-xs gap-2"
          >
            <Trash size={16} /> Delete
          </button>
        )}
      </div>
    );
  };

  function formatDateForInput(date) {
    if (!date || date === "") return "";
    const parsedDate = parseISO(date);
    return isValid(parsedDate) ? format(parsedDate, "yyyy-MM-dd") : "";
  }

  function formatDateForTable(date) {
    if (!date) return "";
    const match = String(date).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return "";
    const [, year, month, day] = match;
    return `${month}/${day}/${year}`;
  }

  function formatLegacyGroup(group) {
    if (!group) return "—";
    return [group.code, group.name].filter(Boolean).join(" — ");
  }

  function formatCalendarDate(date) {
    if (!date) return "-";
    const parsedDate = parseISO(date);
    if (!isValid(parsedDate)) return "-";
    return parsedDate.toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "UTC",
    });
  }

  function getGroupHistoryRows() {
    const studentxgroups = pageData.studentxgroups || [];
    const rows =
      studentxgroups.length > 0
        ? [...studentxgroups]
        : (pageData.studentgroup?.studentxgroups || []).map((studentxgroup) => ({
            ...studentxgroup,
            group: pageData.studentgroup,
          }));

    const currentStudentxgroup = pageData.studentgroup?.studentxgroups?.[0];
    const currentGroupAlreadyListed =
      !pageData.studentgroup ||
      rows.some((studentxgroup) => {
        if (currentStudentxgroup?.id && studentxgroup.id) {
          return studentxgroup.id === currentStudentxgroup.id;
        }
        return (
          studentxgroup.group_id === pageData.studentgroup.id ||
          studentxgroup.group?.id === pageData.studentgroup.id
        );
      });

    if (!currentGroupAlreadyListed &&
        (!currentStudentxgroup || isVisibleStudentGroup(currentStudentxgroup))) {
      rows.unshift({
        ...currentStudentxgroup,
        id: currentStudentxgroup?.id,
        group_id: pageData.studentgroup.id,
        group: pageData.studentgroup,
        start_date:
          currentStudentxgroup?.start_date || pageData.studentgroup.start_date,
        end_date:
          currentStudentxgroup?.end_date || pageData.studentgroup.end_date,
        status: currentStudentxgroup?.status || pageData.studentgroup.status,
      });
    }

    return rows.filter(isVisibleStudentGroup);
  }

  function getGroupHistoryValue(studentxgroup, column) {
    const group = studentxgroup.group || pageData.studentgroup || {};

    switch (column) {
      case "group_name":
        return group.name || "";
      case "start_date":
        return formatDateForInput(studentxgroup.start_date) || "";
      case "end_date":
        return formatDateForInput(studentxgroup.end_date) || "";
      case "teacher":
        return group.staff?.name || pageData.teacher?.name || "";
      case "status":
        return studentxgroup.status || group.status || "";
      case "final_grade":
        return studentxgroup.rotation?.final_grade ?? Number.NEGATIVE_INFINITY;
      case "calculated_result":
        return studentxgroup.rotation?.calculated_result || "";
      default:
        return "";
    }
  }

  function getSortedGroupHistoryRows() {
    const rows = getGroupHistoryRows();
    if (!groupHistorySort.column) return rows;

    return [...rows].sort((a, b) => {
      const left = getGroupHistoryValue(a, groupHistorySort.column);
      const right = getGroupHistoryValue(b, groupHistorySort.column);

      let comparison = 0;

      if (typeof left === "number" && typeof right === "number") {
        comparison = left - right;
      } else {
        comparison = String(left).localeCompare(String(right), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }

      return groupHistorySort.direction === "asc" ? comparison : -comparison;
    });
  }

  function handleGroupHistorySort(column) {
    setGroupHistorySort((prev) => ({
      column,
      direction:
        prev.column === column && prev.direction === "asc" ? "desc" : "asc",
    }));
  }

  function isCurrentGroupHistoryRow(studentxgroup) {
    const status = studentxgroup.status || studentxgroup.group?.status || "";
    const startDate = formatDateForInput(studentxgroup.start_date);
    return !formatDateForInput(studentxgroup.end_date) && status === "Active" &&
      !!startDate && startDate <= format(new Date(), "yyyy-MM-dd");
  }

  function renderGroupHistorySortIcon(column) {
    if (groupHistorySort.column !== column) {
      return <ArrowUpDown size={14} className="text-gray-500" />;
    }

    if (groupHistorySort.direction === "asc") {
      return <ChevronUp size={14} className="text-primary" />;
    }

    return <ChevronDown size={14} className="text-primary" />;
  }

  async function handleEvaluationChartPdf(studentxgroup) {
    const groupId = studentxgroup.group_id || studentxgroup.group?.id;
    if (!groupId) {
      toast("Group is required!", { type: "error", autoClose: 2000 });
      return;
    }

    const loadingKey = `evaluation-${studentxgroup.id || groupId}`;
    setGroupPdfLoading((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const { data } = await api.post(
        "/reports/evaluationChartPDF",
        { group_id: groupId, student_id: id },
        { responseType: "blob" }
      );
      const pdfBlob = new Blob([data], { type: "application/pdf" });
      saveAs(pdfBlob, `evaluation_chart_${groupId}.pdf`);
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to generate PDF.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setGroupPdfLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  }

  async function handleAttendanceReportPdf(studentxgroup) {
    const groupId = studentxgroup.group_id || studentxgroup.group?.id;
    const fromDate = formatDateForInput(studentxgroup.start_date);
    const toDate =
      formatDateForInput(studentxgroup.end_date) ||
      format(new Date(), "yyyy-MM-dd");

    if (!id || id === "new" || !groupId || !fromDate || !toDate) {
      toast("Student and period are required!", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    const loadingKey = `attendance-${studentxgroup.id || studentxgroup.group_id}`;
    setGroupPdfLoading((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const { data } = await api.get(
        `/students/attendanceReport/${id}?group_id=${groupId}&from_date=${fromDate}&to_date=${toDate}`,
        { responseType: "blob" }
      );
      const pdfBlob = new Blob([data], { type: "application/pdf" });
      saveAs(pdfBlob, `attendance_report_${id}.pdf`);
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to generate the report.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setGroupPdfLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  }

  async function handleEvaluationChartLegacyPdf(legacy) {
    const oldRegistrationNumber = pageData.old_registration_number;
    if (!oldRegistrationNumber) {
      toast("Old registration number is required!", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    const groupId = legacy.legacy_group?.id;
    const loadingKey = `evaluation-legacy-${legacy.id}`;
    setGroupPdfLoading((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const { data } = await api.post(
        "/reports/evaluationChartLegacyPDF",
        {
          old_registration_number: oldRegistrationNumber,
          ...(groupId ? { group_id: groupId } : {}),
        },
        { responseType: "blob" }
      );
      const pdfBlob = new Blob([data], { type: "application/pdf" });
      saveAs(pdfBlob, `evaluation_chart_legacy_${oldRegistrationNumber}.pdf`);
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to generate PDF.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setGroupPdfLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  }

  async function handleAttendanceReportLegacyPdf(legacy) {
    const oldRegistrationNumber = pageData.old_registration_number;
    if (!oldRegistrationNumber) {
      toast("Old registration number is required!", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }

    const groupId = legacy.legacy_group?.id;
    const fromDate = formatDateForInput(legacy.start_date);
    const toDate = formatDateForInput(legacy.end_date);
    const loadingKey = `attendance-legacy-${legacy.id}`;
    setGroupPdfLoading((prev) => ({ ...prev, [loadingKey]: true }));
    try {
      const { data } = await api.post(
        "/reports/attendanceReportLegacyPDF",
        {
          old_registration_number: oldRegistrationNumber,
          ...(groupId ? { group_id: groupId } : {}),
          ...(fromDate ? { from_date: fromDate } : {}),
          ...(toDate ? { to_date: toDate } : {}),
        },
        { responseType: "blob" }
      );
      const pdfBlob = new Blob([data], { type: "application/pdf" });
      saveAs(pdfBlob, `attendance_report_legacy_${oldRegistrationNumber}.pdf`);
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to generate the report.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setGroupPdfLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  }

  const groupHistoryRows = getSortedGroupHistoryRows();

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        formType === "preview" ? (
          <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
            <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <Pencil size={16} color="#fff" /> Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <X size={16} /> Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.name}</h2>
            </div>
            <div className="flex flex-1 flex-col items-left px-4 py-2 gap-1">
              <p className="border-b mb-1 pb-1">Prospect Information</p>
              <div className="flex flex-row items-center gap-1 text-xs">
                <strong>Name:</strong> {pageData.name}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-40 gap-4">
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="student-information"
              >
                <GraduationCap size={16} /> Student Information
              </RegisterFormMenu>

              {id !== "new" && (
                <>
                  <RegisterFormMenu
                    setActiveMenu={setActiveMenu}
                    activeMenu={activeMenu}
                    name="student-admission"
                    disabled={!tabAllowed(tabsPermissions, "admission-tab")}
                    messageOnDisabled="You don't have permission to access this tab"
                  >
                    <NotebookPen size={16} /> Admission
                  </RegisterFormMenu>

                  <RegisterFormMenu
                    setActiveMenu={setActiveMenu}
                    activeMenu={activeMenu}
                    name="student-academic"
                    disabled={!tabAllowed(tabsPermissions, "academic-tab")}
                    messageOnDisabled="You don't have permission to access this tab"
                  >
                    <Contact size={16} /> Academic
                  </RegisterFormMenu>

                  <RegisterFormMenu
                    setActiveMenu={setActiveMenu}
                    activeMenu={activeMenu}
                    name="student-dso"
                    disabled={!tabAllowed(tabsPermissions, "dso-tab")}
                    messageOnDisabled="You don't have permission to access this tab"
                  >
                    <BookMarked size={16} /> DSO
                  </RegisterFormMenu>

                  <RegisterFormMenu
                    setActiveMenu={setActiveMenu}
                    activeMenu={activeMenu}
                    name="student-documents"
                    disabled={!tabAllowed(tabsPermissions, "documents-tab")}
                    messageOnDisabled="You don't have permission to access this tab"
                  >
                    <FolderOpen size={16} /> Documents
                  </RegisterFormMenu>

                  {pageData?.enrollments?.length > 0 && (
                    <RegisterFormMenu
                      setActiveMenu={setActiveMenu}
                      activeMenu={activeMenu}
                      name="comments"
                    >
                      <MessageSquare size={16} /> Enrollment Comments
                    </RegisterFormMenu>
                  )}

                  <RegisterFormMenu
                    setActiveMenu={setActiveMenu}
                    activeMenu={activeMenu}
                    name="student-history"
                  >
                    <ClipboardList size={16} /> Student History
                  </RegisterFormMenu>
                </>
              )}
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <InputContext.Provider
                value={{
                  id,
                  generalForm,
                  setSuccessfullyUpdated,
                  fullscreen,
                  setFullscreen,
                  successfullyUpdated,
                  handleCloseForm,
                  handleInactivate,
                  canceled: pageData.canceled_at,
                }}
              >
                <div
                  id="scrollPage"
                  className="flex flex-col items-start justify-start text-sm overflow-y-scroll"
                >
                  <Form
                    ref={generalForm}
                    onSubmit={handleGeneralFormSubmit}
                    className="w-full"
                  >
                    {pageData.loaded ? (
                      <>
                        <FormHeader
                          access={access}
                          title={`${pageData.name || ""} ${
                            pageData.last_name || ""
                          }`.trim()}
                          registry={registry}
                          InputContext={InputContext}
                          otherButtons={
                            pageData.has_overdue_invoice
                              ? [
                                  {
                                    icon: "AlertTriangle",
                                    text: "Resolve Issue",
                                    onClick: handleResolveOverdueInvoiceIssue,
                                  },
                                ]
                              : null
                          }
                        />
                        <InputLineGroup
                          title="student-information"
                          activeMenu={activeMenu === "student-information"}
                        >
                          <FindGeneric
                            route="filials"
                            title="Filial"
                            scope="filial"
                            required
                            InputContext={InputContext}
                            defaultValue={
                              id === "new" && auth.filial.id !== 1
                                ? {
                                    id: auth.filial.id,
                                    name: auth.filial.name,
                                  }
                                : {
                                    id: pageData.filial?.id,
                                    name: pageData.filial?.name,
                                  }
                            }
                            fields={[
                              {
                                title: "Filial Name",
                                name: "name",
                              },
                            ]}
                          />
                          <InputLine title="General Data">
                            <Input
                              type="hidden"
                              name="category"
                              required
                              grow
                              title="Category"
                              defaultValue="Student"
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="registration_number"
                              required
                              readOnly={id !== "new"}
                              title="Registration Number"
                              defaultValue={pageData.registration_number}
                              InputContext={InputContext}
                            />
                            {pageData.old_registration_number != null && (
                              <Input
                                type="text"
                                name="old_registration_number"
                                readOnly
                                title="Old Registration Number"
                                defaultValue={pageData.old_registration_number}
                                InputContext={InputContext}
                              />
                            )}
                            <Input
                              type="text"
                              name="name"
                              required
                              grow
                              title="Name"
                              defaultValue={pageData.name}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="last_name"
                              required
                              grow
                              title="Last Name"
                              defaultValue={pageData.last_name}
                              InputContext={InputContext}
                            />
                            <SelectPopover
                              name="gender"
                              required
                              title="Gender"
                              isSearchable
                              defaultValue={genderOptions.find(
                                (gender) => gender.value === pageData.gender
                              )}
                              options={genderOptions}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine>
                            <DatePicker
                              name="date_of_birth"
                              grow
                              title="Birthday "
                              defaultValue={
                                pageData.date_of_birth
                                  ? (() => {
                                      const parsedDate = parseISO(
                                        pageData.date_of_birth
                                      );
                                      return isValid(parsedDate)
                                        ? parsedDate
                                        : null;
                                    })()
                                  : null
                              }
                              placeholderText="MM/DD/YYYY"
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="passport_number"
                              grow
                              title="Passport Number"
                              placeholder="-----"
                              defaultValue={pageData.passport_number}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="visa_number"
                              grow
                              title="Visa Number"
                              placeholder="-----"
                              defaultValue={pageData.visa_number}
                              InputContext={InputContext}
                            />
                            <DatePicker
                              name="visa_expiration"
                              title="Visa Expiration"
                              defaultValue={
                                pageData.visa_expiration
                                  ? (() => {
                                      const parsedDate = parseISO(
                                        pageData.visa_expiration
                                      );
                                      return isValid(parsedDate)
                                        ? parsedDate
                                        : null;
                                    })()
                                  : null
                              }
                              placeholderText="MM/DD/YYYY"
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="nsevis"
                              title="NSEVIS"
                              grow
                              defaultValue={pageData.nsevis}
                              placeholder="-----"
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine>
                            <Input
                              type="text"
                              name="birth_country"
                              title="Country of Birth"
                              grow
                              defaultValue={pageData.birth_country}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="birth_state"
                              title="State / Province of Birth"
                              grow
                              defaultValue={pageData.birth_state}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="birth_city"
                              title="City of Birth"
                              grow
                              defaultValue={pageData.birth_city}
                              InputContext={InputContext}
                            />
                          </InputLine>

                          <FindGeneric
                            route="agents"
                            title="Associate"
                            scope="agent"
                            required
                            InputContext={InputContext}
                            defaultValue={{
                              id: pageData.agent?.id,
                              name: pageData.agent?.name,
                            }}
                            fields={[
                              {
                                title: "Associate Name",
                                name: "name",
                              },
                            ]}
                          />

                          <FindGeneric
                            route="partners_and_influencers"
                            title="Partner and Influencer"
                            scope="partners_and_influencers"
                            InputContext={InputContext}
                            defaultValue={{
                              id: pageData.partners_and_influencers?.id,
                              name: pageData.partners_and_influencers
                                ?.partners_name,
                            }}
                            fields={[
                              {
                                title: "Partner Name",
                                name: "partners_name",
                              },
                            ]}
                          />
                          <InputLine title="Enrollment">
                            <SelectPopover
                              name="status"
                              required
                              grow
                              title="Status"
                              readOnly={id !== "new"}
                              isSearchable
                              defaultValue={optionsStatus.find(
                                (opt) => opt.value === pageData.status
                              )}
                              options={optionsStatus}
                              InputContext={InputContext}
                            />
                            {pageData.status === "Inactive" && (
                              <>
                                <SelectPopover
                                  name="inactive_reason"
                                  grow
                                  readOnly={id !== "new"}
                                  title="Inactivation Reason"
                                  defaultValue={inactiveReasonsOptions.find(
                                    (opt) =>
                                      opt.value === pageData.inactive_reason
                                  )}
                                  options={inactiveReasonsOptions}
                                  InputContext={InputContext}
                                />
                                <SelectPopover
                                  name="inactive_reason_description"
                                  grow
                                  readOnly={id !== "new"}
                                  title="Inactivation Reason Description"
                                  defaultValue={inactivateReasonsDescriptions.find(
                                    (opt) =>
                                      opt.value ===
                                      pageData.inactive_reason_description
                                  )}
                                  options={inactivateReasonsDescriptions}
                                  InputContext={InputContext}
                                />
                                <DatePicker
                                  disabled={true}
                                  grow
                                  name="inactivation_date"
                                  title="Inactivation Date"
                                  InputContext={InputContext}
                                  defaultValue={
                                    pageData.inactivation_date &&
                                    pageData.inactivation_date !== ""
                                      ? (() => {
                                          const parsedDate = parseISO(
                                            pageData.inactivation_date
                                          );
                                          return isValid(parsedDate)
                                            ? format(parsedDate, "MM/dd/yyyy")
                                            : null;
                                        })()
                                      : null
                                  }
                                />
                              </>
                            )}
                            <SelectPopover
                              name="category"
                              required
                              grow
                              title="Category"
                              readOnly={id !== "new"}
                              isSearchable
                              defaultValue={optionsCategory.find(
                                (opt) => opt.value === pageData.category
                              )}
                              options={optionsCategory}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <FindGeneric
                            route="processtypes"
                            title="Process Type"
                            scope="processtypes"
                            required
                            readOnly={id !== "new"}
                            InputContext={InputContext}
                            defaultValue={{
                              id: pageData.processtypes?.id,
                              name: pageData.processtypes?.name,
                            }}
                            fields={[
                              {
                                title: "Name",
                                name: "name",
                              },
                            ]}
                          />
                          <FindGeneric
                            route="processsubstatuses"
                            title="Process Sub Status"
                            scope="processsubstatuses"
                            required
                            readOnly={id !== "new"}
                            InputContext={InputContext}
                            defaultValue={{
                              id: pageData.processsubstatuses?.id,
                              name: pageData.processsubstatuses?.name,
                            }}
                            fields={[
                              {
                                title: "Name",
                                name: "name",
                              },
                            ]}
                          />

                          <PricesSimulation
                            student={pageData}
                            InputContext={InputContext}
                            FullGridContext={FullGridContext}
                            generalForm={generalForm}
                            showAdmissionDiscounts={true}
                            isAdmissionDiscountChangable={true}
                            showFinancialDiscounts={true}
                            isFinancialDiscountChangable={false}
                          />

                          <InputLine title="U.S. Location">
                            <Input
                              type="text"
                              name="address"
                              title="Address"
                              grow
                              defaultValue={pageData.address}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="zip"
                              grow
                              title="Zip Code"
                              defaultValue={pageData.zip}
                              InputContext={InputContext}
                            />
                            {/* <SelectPopover
                              name="birth_country"
                              grow
                              title="Country"
                              options={countriesOptions}
                              isSearchable
                              defaultValue={countriesOptions.find(
                                (country) =>
                                  country.value === pageData.birth_country
                              )}
                              InputContext={InputContext}
                            /> */}
                            <Input
                              type="text"
                              name="state"
                              grow
                              title="State"
                              defaultValue={pageData.state}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="city"
                              grow
                              title="City"
                              defaultValue={pageData.city}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="Home Country">
                            <Input
                              type="text"
                              name="home_country_address"
                              title="Address"
                              grow
                              defaultValue={pageData.home_country_address}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="home_country_zip"
                              grow
                              title="Zip Code"
                              defaultValue={pageData.home_country_zip}
                              InputContext={InputContext}
                            />
                            <SelectPopover
                              name="home_country_country"
                              grow
                              title="Country"
                              options={countriesOptions}
                              isSearchable
                              defaultValue={countriesOptions.find(
                                (country) =>
                                  country.value ===
                                  pageData.home_country_country
                              )}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="home_country_state"
                              grow
                              title="State"
                              defaultValue={pageData.home_country_state}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="home_country_city"
                              grow
                              title="City"
                              defaultValue={pageData.home_country_city}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="Contact">
                            <Input
                              type="text"
                              name="email"
                              required
                              title="E-mail"
                              grow
                              defaultValue={pageData.email}
                              InputContext={InputContext}
                            />
                            <PhoneNumberInput
                              type="text"
                              grow
                              name="whatsapp"
                              title="Whatsapp"
                              isPhoneNumber
                              value={pageData.whatsapp}
                              InputContext={InputContext}
                            />
                            <PhoneNumberInput
                              type="text"
                              grow
                              name="phone"
                              title="U.S. Phone"
                              value={pageData.phone}
                              InputContext={InputContext}
                            />
                            <PhoneNumberInput
                              type="text"
                              grow
                              name="home_country_phone"
                              title="Home Country Phone"
                              value={pageData.home_country_phone}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine>
                            <SelectPopover
                              name="preferred_contact_form"
                              grow
                              title="Preferred Contact Form"
                              isSearchable
                              defaultValue={optionsContactForm.find(
                                (opt) =>
                                  opt.value === pageData.preferred_contact_form
                              )}
                              options={optionsContactForm}
                              InputContext={InputContext}
                            />
                          </InputLine>
                        </InputLineGroup>
                        <InputLineGroup
                          title="student-admission"
                          activeMenu={activeMenu === "student-admission"}
                        >
                          {/* Sub-tabs: Admission | Enrollments */}
                          <div className="flex flex-row border-b w-full mb-2">
                            <button
                              type="button"
                              onClick={() => setAdmissionSubTab("admission")}
                              className={`px-4 py-2 text-xs font-semibold border-b-2 transition-colors ${admissionSubTab === "admission" ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                            >
                              Admission
                            </button>
                            <button
                              type="button"
                              onClick={() => setAdmissionSubTab("enrollments")}
                              className={`px-4 py-2 text-xs font-semibold border-b-2 transition-colors ${admissionSubTab === "enrollments" ? "border-primary text-primary" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                            >
                              Enrollments
                            </button>
                          </div>

                          {admissionSubTab === "admission" && (
                            <>
                              <InputLine title="Admission">
                                <DatePicker
                                  name="enrollment_date"
                                  grow
                                  title="Enrollment Date"
                                  defaultValue={pageData.enrollment_date ? parseISO(pageData.enrollment_date) : null}
                                  placeholderText="MM/DD/YYYY"
                                  readOnly
                                  InputContext={InputContext}
                                />
                                <DatePicker
                                  name="expected_start_date"
                                  grow
                                  title="Expected Start Date"
                                  defaultValue={pageData.expected_start_date ? parseISO(pageData.expected_start_date) : null}
                                  placeholderText="MM/DD/YYYY"
                                  readOnly
                                  InputContext={InputContext}
                                />
                              </InputLine>
                              <InputLine>
                                <FindGeneric
                                  route="agents"
                                  title="Agent"
                                  scope="agent"
                                  InputContext={InputContext}
                                  defaultValue={{
                                    id: pageData.agent?.id,
                                    name: pageData.agent?.name,
                                  }}
                                  fields={[
                                    {
                                      title: "Agent Name",
                                      name: "name",
                                    },
                                  ]}
                                />
                              </InputLine>
                              <InputLine>
                                <Input
                                  name="how_did_you_hear_about_mila"
                                  grow
                                  title="How did you hear about MILA?"
                                  defaultValue={pageData.how_did_you_hear_about_mila}
                                  InputContext={InputContext}
                                />
                              </InputLine>
                            </>
                          )}

                          {admissionSubTab === "enrollments" && (
                            <div className="w-full px-2 py-2">
                              {loadingEnrollmentHistory ? (
                                <p className="text-xs text-gray-400 py-4">Loading...</p>
                              ) : (
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="bg-gray-100">
                                      <th className="w-6"></th>
                                      <th className="text-left px-3 py-2">Application</th>
                                      <th className="text-left px-3 py-2">Status</th>
                                      <th className="text-left px-3 py-2">Process Type</th>
                                      <th className="text-left px-3 py-2">Sub-status</th>
                                      <th className="text-left px-3 py-2">Contract Date</th>
                                      <th className="text-left px-3 py-2">Created At</th>
                                      <th className="text-left px-3 py-2"></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {enrollmentHistory.length === 0 ? (
                                      <tr>
                                        <td colSpan={8} className="text-center text-gray-400 py-4">No enrollments found.</td>
                                      </tr>
                                    ) : (
                                      enrollmentHistory.map((enrollment) => {
                                        const isOpen = selectedEnrollment?.id === enrollment.id;
                                        return (
                                          <React.Fragment key={enrollment.id}>
                                            <tr
                                              onClick={() => setSelectedEnrollment(isOpen ? null : enrollment)}
                                              className="cursor-pointer border-b even:bg-gray-50 hover:bg-blue-50 transition-colors"
                                            >
                                              <td className="px-2 py-2 text-gray-400">
                                                {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                              </td>
                                              <td className="px-3 py-2">{enrollment.application ?? "-"}</td>
                                              <td className="px-3 py-2">{enrollment.status ?? "-"}</td>
                                              <td className="px-3 py-2">{enrollment.contract?.processtypes?.name ?? "-"}</td>
                                              <td className="px-3 py-2">{enrollment.contract?.processsubstatuses?.name ?? "-"}</td>
                                              <td className="px-3 py-2">{formatCalendarDate(enrollment.contract?.date)}</td>
                                              <td className="px-3 py-2">{formatCalendarDate(enrollment.created_at)}</td>
                                              <td className="px-3 py-2">
                                                {isEnrollmentProcess(enrollment) && (
                                                  <button
                                                    type="button"
                                                    disabled={downloadingEnrollmentPDF === enrollment.id}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleEnrollmentPDF(enrollment);
                                                    }}
                                                    className="bg-slate-300 text-slate-500 border border-slate-400 hover:bg-slate-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed rounded-md py-3 px-2 h-6 flex flex-row items-center justify-start text-xs gap-2 cursor-pointer whitespace-nowrap"
                                                  >
                                                    <Download size={14} />
                                                    <strong>Download PDF - Novo</strong>
                                                  </button>
                                                )}
                                              </td>
                                            </tr>
                                            {isOpen && (
                                              <tr>
                                                <td colSpan={8} className="bg-gray-50 px-4 py-4 border-b">
                                                  <div className="flex flex-col gap-3">
                                                    {/* Contract */}
                                                    <div className="border rounded-lg p-3 bg-white">
                                                      <p className="text-xs font-semibold text-gray-700 mb-2">Contract</p>
                                                      <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                                                        <div><span className="text-gray-500">Contract Status:</span> <span className="font-medium">{enrollment.contract?.status ?? "-"}</span></div>
                                                        <div><span className="text-gray-500">Process Type:</span> <span className="font-medium">{enrollment.contract?.processtypes?.name ?? "-"}</span></div>
                                                        <div><span className="text-gray-500">Sub-status:</span> <span className="font-medium">{enrollment.contract?.processsubstatuses?.name ?? "-"}</span></div>
                                                        <div><span className="text-gray-500">Contract Date:</span> <span className="font-medium">{formatCalendarDate(enrollment.contract?.date)}</span></div>
                                                        <div><span className="text-gray-500">Created At:</span> <span className="font-medium">{formatCalendarDate(enrollment.contract?.created_at)}</span></div>
                                                      </div>
                                                    </div>
                                                    {/* Activation Information */}
                                                    <div className="border rounded-lg p-3 bg-white">
                                                      <p className="text-xs font-semibold text-gray-700 mb-2">Activation Information</p>
                                                      {enrollment.contract?.activation ? (
                                                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                                                          <div><span className="text-gray-500">Activation Date:</span> <span className="font-medium">{formatCalendarDate(enrollment.contract.activation.activation_date)}</span></div>
                                                          <div><span className="text-gray-500">Student Group ID:</span> <span className="font-medium">{enrollment.contract.activation.student_group_id ?? "-"}</span></div>
                                                          <div><span className="text-gray-500">Memo:</span> <span className="font-medium">{enrollment.contract.activation.memo ?? "-"}</span></div>
                                                          <div><span className="text-gray-500">Created At:</span> <span className="font-medium">{formatCalendarDate(enrollment.contract.activation.created_at)}</span></div>
                                                        </div>
                                                      ) : (
                                                        <p className="text-xs text-gray-400">No activation information.</p>
                                                      )}
                                                    </div>
                                                    {/* Inactivation Information */}
                                                    <div className="border rounded-lg p-3 bg-white">
                                                      <p className="text-xs font-semibold text-gray-700 mb-2">Inactivation Information</p>
                                                      {enrollment.contract?.inactivation ? (
                                                        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                                                          <div><span className="text-gray-500">Reason:</span> <span className="font-medium">{enrollment.contract.inactivation.inactivation_reason?.name ?? enrollment.contract.inactivation.reason ?? "-"}</span></div>
                                                          <div><span className="text-gray-500">Description:</span> <span className="font-medium">{enrollment.contract.inactivation.inactivation_reason_description?.name ?? enrollment.contract.inactivation.reason_description ?? "-"}</span></div>
                                                          <div><span className="text-gray-500">Option:</span> <span className="font-medium">{enrollment.contract.inactivation.inactivation_reason_description_option?.name ?? "-"}</span></div>
                                                          <div><span className="text-gray-500">Date:</span> <span className="font-medium">{formatCalendarDate(enrollment.contract.inactivation.date)}</span></div>
                                                          <div><span className="text-gray-500">Memo:</span> <span className="font-medium">{enrollment.contract.inactivation.memo ?? "-"}</span></div>
                                                          <div><span className="text-gray-500">Created At:</span> <span className="font-medium">{formatCalendarDate(enrollment.contract.inactivation.created_at)}</span></div>
                                                        </div>
                                                      ) : (
                                                        <p className="text-xs text-gray-400">No inactivation information.</p>
                                                      )}
                                                    </div>
                                                    {/* Contract Sessions (Non-F1 / Global) */}
                                                    {Array.isArray(enrollment.contract?.sessions) && enrollment.contract.sessions.length > 0 && (
                                                      <div className="border rounded-lg p-3 bg-white">
                                                        <p className="text-xs font-semibold text-gray-700 mb-2">Contract Sessions</p>
                                                        <table className="w-full text-xs border-collapse">
                                                          <thead>
                                                            <tr className="bg-gray-100">
                                                              <th className="text-left px-2 py-1">#</th>
                                                              <th className="text-left px-2 py-1">Start Date</th>
                                                              <th className="text-left px-2 py-1">End Date</th>
                                                              <th className="text-left px-2 py-1">Plan Schedule</th>
                                                              <th className="text-left px-2 py-1">Payment</th>
                                                              <th className="text-right px-2 py-1">Tuition</th>
                                                              <th className="text-right px-2 py-1">Discount</th>
                                                              <th className="text-left px-2 py-1">Status</th>
                                                            </tr>
                                                          </thead>
                                                          <tbody>
                                                            {enrollment.contract.sessions.map((session, idx) => (
                                                              <tr key={session.id ?? idx} className="border-b even:bg-gray-50">
                                                                <td className="px-2 py-1">{idx + 1}</td>
                                                                <td className="px-2 py-1">{formatCalendarDate(session.session_start_date)}</td>
                                                                <td className="px-2 py-1">{formatCalendarDate(session.session_end_date)}</td>
                                                                <td className="px-2 py-1">{session.plan_schedule ?? "-"}</td>
                                                                <td className="px-2 py-1 capitalize">{session.payment_type ?? "-"}</td>
                                                                <td className="px-2 py-1 text-right">${parseFloat(session.tuition_amount || 0).toFixed(2)}</td>
                                                                <td className="px-2 py-1 text-right">${parseFloat(session.tuition_discount || 0).toFixed(2)}</td>
                                                                <td className="px-2 py-1 capitalize">
                                                                  <span className={session.status === "active" ? "text-green-600 font-semibold" : "text-gray-500"}>
                                                                    {session.status ?? "-"}
                                                                  </span>
                                                                </td>
                                                              </tr>
                                                            ))}
                                                          </tbody>
                                                        </table>
                                                      </div>
                                                    )}
                                                  </div>
                                                </td>
                                              </tr>
                                            )}
                                          </React.Fragment>
                                        );
                                      })
                                    )}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </InputLineGroup>
                        <InputLineGroup
                          title="student-academic"
                          activeMenu={activeMenu === "student-academic"}
                        >
                          <Scope path="enrollment">
                            <InputLine title="Placement Test">
                              <Input
                                type="text"
                                name="question_level"
                                grow
                                title="Question Level"
                                defaultValue={
                                  pageData?.enrollments[0]?.question_level
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="reading_score_percentage"
                                grow
                                title="Reading Score %"
                                defaultValue={
                                  pageData?.enrollments[0]
                                    ?.reading_score_percentage
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="writing_score"
                                grow
                                title="Writing Score"
                                defaultValue={
                                  pageData?.enrollments[0]?.writing_score
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="writing_score_percentage"
                                grow
                                title="Writing Score %"
                                defaultValue={
                                  pageData?.enrollments[0]
                                    ?.writing_score_percentage
                                }
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine>
                              <Input
                                type="text"
                                name="reading_writing_score"
                                grow
                                title="Reading Writing Score %"
                                defaultValue={
                                  pageData?.enrollments[0]
                                    ?.reading_writing_score
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="speaking_score"
                                grow
                                title="Speaking Score"
                                defaultValue={
                                  pageData?.enrollments[0]?.speaking_score
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="speaking_score_percentage"
                                grow
                                title="Speaking Score %"
                                defaultValue={
                                  pageData?.enrollments[0]
                                    ?.speaking_score_percentage
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="speaking_level"
                                grow
                                title="Speaking Level"
                                defaultValue={
                                  pageData?.enrollments[0]?.speaking_level
                                }
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine>
                              <Input
                                type="text"
                                name="academic_decision"
                                grow
                                title="Academic Overall Decision"
                                defaultValue={
                                  pageData?.enrollments[0]?.academic_decision
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="final_level"
                                grow
                                title="Final Level"
                                defaultValue={
                                  pageData?.enrollments[0]?.final_level
                                }
                                InputContext={InputContext}
                              />
                            </InputLine>
                            <InputLine>
                              <Textarea
                                name="academic_notes"
                                grow
                                rows={3}
                                title="Academic Notes"
                                defaultValue={
                                  pageData?.enrollments[0]?.academic_notes
                                }
                                InputContext={InputContext}
                              />
                              <Textarea
                                name="academic_feedback"
                                grow
                                rows={3}
                                title="Academic Feedback"
                                defaultValue={
                                  pageData?.enrollments[0]?.academic_feedback
                                }
                                InputContext={InputContext}
                              />
                            </InputLine>
                          </Scope>
                          <InputLine title="History">
                            <div className="w-full flex flex-row gap-1 border-b border-gray-200">
                              <button
                                type="button"
                                onClick={() =>
                                  setAcademicHistoryTab("group-history")
                                }
                                className={`px-4 py-2 text-xs font-bold rounded-t-md transition-colors ${
                                  academicHistoryTab === "group-history"
                                    ? "bg-secondary text-gray-800 border border-b-0 border-gray-200"
                                    : "text-gray-400 hover:text-gray-600"
                                }`}
                              >
                                Group History
                              </button>
                              {pageData.legacy_history &&
                                pageData.legacy_history.length > 0 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setAcademicHistoryTab("legacy")
                                    }
                                    className={`px-4 py-2 text-xs font-bold rounded-t-md transition-colors ${
                                      academicHistoryTab === "legacy"
                                        ? "bg-secondary text-gray-800 border border-b-0 border-gray-200"
                                        : "text-gray-400 hover:text-gray-600"
                                    }`}
                                  >
                                    Legacy
                                  </button>
                                )}
                            </div>
                          </InputLine>
                          {academicHistoryTab === "legacy" &&
                            pageData.legacy_history &&
                            pageData.legacy_history.length > 0 && (
                              <InputLine title="Legacy">
                                <div className="w-full">
                                  <table className="relative bg-secondary-50 rounded-xl p-4 w-full table-auto text-xs overflow-hidden text-left">
                                    <thead className="sticky top-0 border-md">
                                      <tr className="bg-secondary h-8">
                                        <th className="px-4">Event Date</th>
                                        <th className="px-4">Type</th>
                                        <th className="px-4">Group</th>
                                        <th className="px-4">Seq.</th>
                                        <th className="px-4">Start Date</th>
                                        <th className="px-4">End Date</th>
                                        <th className="px-4">Entry Reason</th>
                                        <th className="px-4">Exit Reason</th>
                                        <th className="px-4">Status</th>
                                        <th className="px-4">PDF</th>
                                      </tr>
                                    </thead>
                                    <tbody className="align-center">
                                      {pageData.legacy_history.map(
                                        (legacy) => {
                                        const evaluationLegacyLoadingKey = `evaluation-legacy-${legacy.id}`;
                                        const attendanceLegacyLoadingKey = `attendance-legacy-${legacy.id}`;
                                        return (
                                          <tr
                                            key={legacy.id}
                                            className="border-t h-8 odd:bg-white"
                                          >
                                            <td className="px-4">
                                              {formatDateForTable(
                                                legacy.event_date
                                              ) || "—"}
                                            </td>
                                            <td className="px-4">
                                              {legacy.type
                                                ? legacy.type.replaceAll(
                                                    "_",
                                                    " "
                                                  )
                                                : "—"}
                                            </td>
                                            <td className="px-4">
                                              {legacy.old_legacy_group ||
                                              legacy.new_legacy_group ? (
                                                <span>
                                                  {formatLegacyGroup(legacy.old_legacy_group)}
                                                  {" → "}
                                                  {formatLegacyGroup(legacy.new_legacy_group)}
                                                </span>
                                              ) : (
                                                formatLegacyGroup(legacy.legacy_group)
                                              )}
                                            </td>
                                            <td className="px-4">
                                              {legacy.sequence ?? "—"}
                                            </td>
                                            <td className="px-4">
                                              {formatDateForTable(
                                                legacy.start_date
                                              ) || "—"}
                                            </td>
                                            <td className="px-4">
                                              {formatDateForTable(
                                                legacy.end_date
                                              ) || "—"}
                                            </td>
                                            <td className="px-4">
                                              {legacy.entry_reason || "—"}
                                            </td>
                                            <td className="px-4">
                                              {legacy.exit_reason || "—"}
                                            </td>
                                            <td className="px-4">
                                              {legacy.status_at_last_event ||
                                                "—"}
                                            </td>
                                            <td className="px-4">
                                              <div className="flex flex-row items-center gap-2">
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleEvaluationChartLegacyPdf(
                                                      legacy
                                                    )
                                                  }
                                                  disabled={
                                                    groupPdfLoading[
                                                      evaluationLegacyLoadingKey
                                                    ]
                                                  }
                                                  className="text-md font-bold bg-secondary border text-zinc-500 hover:border-primary hover:text-primary rounded-md px-2 h-7 flex flex-row items-center justify-center text-xs gap-1 disabled:opacity-50"
                                                >
                                                  {groupPdfLoading[
                                                    evaluationLegacyLoadingKey
                                                  ] ? (
                                                    <Loader2
                                                      size={14}
                                                      className="animate-spin"
                                                    />
                                                  ) : (
                                                    <FileText size={14} />
                                                  )}
                                                  Evaluation Chart
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleAttendanceReportLegacyPdf(
                                                      legacy
                                                    )
                                                  }
                                                  disabled={
                                                    groupPdfLoading[
                                                      attendanceLegacyLoadingKey
                                                    ]
                                                  }
                                                  className="text-md font-bold bg-secondary border text-zinc-500 hover:border-primary hover:text-primary rounded-md px-2 h-7 flex flex-row items-center justify-center text-xs gap-1 disabled:opacity-50"
                                                >
                                                  {groupPdfLoading[
                                                    attendanceLegacyLoadingKey
                                                  ] ? (
                                                    <Loader2
                                                      size={14}
                                                      className="animate-spin"
                                                    />
                                                  ) : (
                                                    <FileText size={14} />
                                                  )}
                                                  Attendance Report
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </InputLine>
                            )}
                          {academicHistoryTab === "group-history" && (
                          <InputLine title="Group History">
                            <div className="w-full">
                              <table className="relative bg-secondary-50 rounded-xl p-4 w-full table-auto text-xs overflow-hidden text-left">
                                <thead className="sticky top-0 border-md">
                                  <tr className="bg-secondary h-8">
                                    <th className="px-4">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleGroupHistorySort("group_name")
                                        }
                                        className="flex items-center gap-1 font-semibold"
                                      >
                                        Group Name
                                        {renderGroupHistorySortIcon(
                                          "group_name"
                                        )}
                                      </button>
                                    </th>
                                    <th className="px-4">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleGroupHistorySort("start_date")
                                        }
                                        className="flex items-center gap-1 font-semibold"
                                      >
                                        Start Date
                                        {renderGroupHistorySortIcon(
                                          "start_date"
                                        )}
                                      </button>
                                    </th>
                                    <th className="px-4">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleGroupHistorySort("end_date")
                                        }
                                        className="flex items-center gap-1 font-semibold"
                                      >
                                        End Date
                                        {renderGroupHistorySortIcon("end_date")}
                                      </button>
                                    </th>
                                    <th className="px-4">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleGroupHistorySort("teacher")
                                        }
                                        className="flex items-center gap-1 font-semibold"
                                      >
                                        Teacher
                                        {renderGroupHistorySortIcon("teacher")}
                                      </button>
                                    </th>
                                    <th className="px-4">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleGroupHistorySort("status")
                                        }
                                        className="flex items-center gap-1 font-semibold"
                                      >
                                        Status
                                        {renderGroupHistorySortIcon("status")}
                                      </button>
                                    </th>
                                    <th className="px-4">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleGroupHistorySort("final_grade")
                                        }
                                        className="flex items-center gap-1 font-semibold"
                                      >
                                        Final Grade
                                        {renderGroupHistorySortIcon(
                                          "final_grade"
                                        )}
                                      </button>
                                    </th>
                                    <th className="px-4">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleGroupHistorySort(
                                            "calculated_result"
                                          )
                                        }
                                        className="flex items-center gap-1 font-semibold"
                                      >
                                        Calculated Result
                                        {renderGroupHistorySortIcon(
                                          "calculated_result"
                                        )}
                                      </button>
                                    </th>
                                    <th className="px-4">PDF</th>
                                  </tr>
                                </thead>
                                <tbody className="align-center">
                                  {groupHistoryRows.length > 0 ? (
                                    groupHistoryRows.map(
                                      (studentxgroup, index) => {
                                        const group =
                                          studentxgroup.group ||
                                          pageData.studentgroup ||
                                          {};
                                        const isCurrentRow =
                                          isCurrentGroupHistoryRow(
                                            studentxgroup
                                          );
                                        const isScheduledRow =
                                          !studentxgroup.canceled_at &&
                                          !formatDateForInput(studentxgroup.end_date) &&
                                          formatDateForInput(studentxgroup.start_date) > format(new Date(), "yyyy-MM-dd");
                                        const rowKey =
                                          studentxgroup.id ||
                                          `${studentxgroup.group_id}-${studentxgroup.start_date}-${index}`;
                                        const evaluationLoadingKey = `evaluation-${
                                          studentxgroup.id ||
                                          studentxgroup.group_id ||
                                          group.id
                                        }`;
                                        const attendanceLoadingKey = `attendance-${
                                          studentxgroup.id ||
                                          studentxgroup.group_id ||
                                          group.id
                                        }`;

                                        return (
                                          <tr
                                            key={rowKey}
                                            className={`h-10 odd:bg-white hover:rounded-sm hover:border hover:border-mila_orange ${
                                              isCurrentRow
                                                ? "bg-emerald-50 ring-1 ring-inset ring-emerald-300"
                                                : ""
                                            }`}
                                          >
                                            <td className="px-4">
                                              <div className="flex items-center gap-2">
                                                <span>
                                                  {group.name ||
                                                    (studentxgroup.group_id
                                                      ? `Group #${studentxgroup.group_id}`
                                                      : "")}
                                                </span>
                                                {isCurrentRow && (
                                                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
                                                    Current
                                                  </span>
                                                )}
                                                {isScheduledRow && (
                                                  <span className="rounded-full bg-orange-100 px-2 py-0.5 text-[11px] font-semibold text-orange-700">
                                                    Scheduled
                                                  </span>
                                                )}
                                              </div>
                                            </td>
                                            <td className="px-4">
                                              {formatDateForTable(
                                                studentxgroup.start_date
                                              )}
                                            </td>
                                            <td className="px-4">
                                              {formatDateForTable(
                                                studentxgroup.end_date
                                              )}
                                            </td>
                                            <td className="px-4">
                                              {group.staff?.name ||
                                                pageData.teacher?.name ||
                                                ""}
                                            </td>
                                            <td className="px-4">
                                              <div className="flex items-center gap-2">
                                                <span>
                                                  {isScheduledRow && studentxgroup.status === "Active"
                                                    ? "Not started"
                                                    : studentxgroup.status ||
                                                    group.status ||
                                                    ""}
                                                </span>
                                                {isCurrentRow && (
                                                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                                                )}
                                              </div>
                                            </td>
                                            <td className="px-4">
                                              {studentxgroup.rotation
                                                ?.final_grade ?? ""}
                                            </td>
                                            <td className="px-4">
                                              {studentxgroup.rotation
                                                ?.calculated_result || ""}
                                            </td>
                                            <td className="px-4">
                                              <div className="flex flex-row items-center gap-2">
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleEvaluationChartPdf(
                                                      studentxgroup
                                                    )
                                                  }
                                                  disabled={
                                                    groupPdfLoading[
                                                      evaluationLoadingKey
                                                    ]
                                                  }
                                                  className="text-md font-bold bg-secondary border text-zinc-500 hover:border-primary hover:text-primary rounded-md px-2 h-7 flex flex-row items-center justify-center text-xs gap-1 disabled:opacity-50"
                                                >
                                                  {groupPdfLoading[
                                                    evaluationLoadingKey
                                                  ] ? (
                                                    <Loader2
                                                      size={14}
                                                      className="animate-spin"
                                                    />
                                                  ) : (
                                                    <FileText size={14} />
                                                  )}
                                                  Evaluation Chart
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleAttendanceReportPdf(
                                                      studentxgroup
                                                    )
                                                  }
                                                  disabled={
                                                    groupPdfLoading[
                                                      attendanceLoadingKey
                                                    ]
                                                  }
                                                  className="text-md font-bold bg-secondary border text-zinc-500 hover:border-primary hover:text-primary rounded-md px-2 h-7 flex flex-row items-center justify-center text-xs gap-1 disabled:opacity-50"
                                                >
                                                  {groupPdfLoading[
                                                    attendanceLoadingKey
                                                  ] ? (
                                                    <Loader2
                                                      size={14}
                                                      className="animate-spin"
                                                    />
                                                  ) : (
                                                    <FileText size={14} />
                                                  )}
                                                  Attendance Report
                                                </button>
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      }
                                    )
                                  ) : (
                                    <tr className="bg-gray-50 text-gray-500 h-10">
                                      <td className="px-4" colSpan="8">
                                        There&apos;s nothing here yet.
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </InputLine>
                          )}
                        </InputLineGroup>
                        <InputLineGroup
                          title="student-dso"
                          activeMenu={activeMenu === "student-dso"}
                        >
                          <InputLine title="DSO Information">
                            <Input
                              type="date"
                              name="start_date"
                              grow
                              readOnly
                              title="Student Start Date"
                              defaultValue={
                                pageData.start_date &&
                                pageData.start_date !== ""
                                  ? (() => {
                                      const parsedDate = parseISO(
                                        pageData.start_date
                                      );
                                      return isValid(parsedDate)
                                        ? format(parsedDate, "yyyy-MM-dd")
                                        : null;
                                    })()
                                  : null
                              }
                              InputContext={InputContext}
                            />

                            {pageData?.programs &&
                              pageData?.programs?.length > 0 && (
                                <>
                                  <Input
                                    type="date"
                                    name="program_start_date"
                                    grow
                                    readOnly
                                    title="Program Start Date"
                                    defaultValue={
                                      pageData?.programs[0].start_date &&
                                      pageData?.programs[0].start_date !== ""
                                        ? (() => {
                                            const parsedDate = parseISO(
                                              pageData?.programs[0].start_date
                                            );
                                            return isValid(parsedDate)
                                              ? format(parsedDate, "yyyy-MM-dd")
                                              : null;
                                          })()
                                        : null
                                    }
                                    InputContext={InputContext}
                                  />

                                  <Input
                                    type="date"
                                    name="program_end_date"
                                    grow
                                    readOnly
                                    title="Program End Date"
                                    defaultValue={
                                      pageData?.programs[0].end_date &&
                                      pageData?.programs[0].end_date !== ""
                                        ? (() => {
                                            const parsedDate = parseISO(
                                              pageData?.programs[0].end_date
                                            );
                                            return isValid(parsedDate)
                                              ? format(parsedDate, "yyyy-MM-dd")
                                              : null;
                                          })()
                                        : null
                                    }
                                    InputContext={InputContext}
                                  />
                                </>
                              )}
                          </InputLine>
                          {(tabAllowed(tabsPermissions, "dso-tab")
                            ?.MenuHierarchyXGroup?.create ||
                            tabAllowed(tabsPermissions, "dso-tab")
                              ?.MenuHierarchyXGroup?.edit) && (
                            <InputLine title="New I20">
                              {!altProgramMode ? (
                                <>
                                  <div className="flex flex-col justify-center items-start relative w-full md:w-auto">
                                    <div className="px-2 text-xs flex flex-row justify-between items-center">
                                      I-20
                                    </div>
                                    {pageData?.programs?.[0]?.i20?.url ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          window.open(
                                            pageData?.programs?.[0]?.i20?.url
                                          )
                                        }
                                        className="px-4 py-2 text-xs font-bold border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white rounded-sm flex flex-row items-center gap-2"
                                      >
                                        <Download size={14} />
                                        <span>Open I-20</span>
                                      </button>
                                    ) : (
                                      <div className="px-4 py-2 text-xs text-gray-500 border border-gray-300 rounded-sm bg-slate-100">
                                        No I-20 file
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex flex-col justify-center items-start relative w-full md:w-auto grow">
                                    <div className="px-2 text-xs flex flex-row justify-between items-center">
                                      Program Start Date
                                    </div>
                                    <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row justify-between items-center gap-2 bg-slate-100">
                                      <input
                                        type="date"
                                        className="bg-transparent w-full"
                                        value={altProgramStartDate}
                                        readOnly
                                      />
                                    </div>
                                  </div>
                                  <div className="flex flex-col justify-center items-start relative w-full md:w-auto grow">
                                    <div className="px-2 text-xs flex flex-row justify-between items-center">
                                      Program End Date
                                    </div>
                                    <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row justify-between items-center gap-2 bg-slate-100">
                                      <input
                                        type="date"
                                        className="bg-transparent w-full"
                                        value={altProgramEndDate}
                                        readOnly
                                      />
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <>
                                  {altProgramFileDeleted ||
                                  altProgramMode === "add-new" ? (
                                    <FileInput
                                      name="alt_program_file"
                                      title="I-20"
                                      grow
                                      InputContext={InputContext}
                                    />
                                  ) : (
                                    <div className="flex flex-col justify-center items-start relative w-full md:w-auto">
                                      <div className="px-2 text-xs flex flex-row justify-between items-center">
                                        I-20
                                      </div>
                                      <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row items-center justify-between gap-2 bg-slate-100">
                                        <span className="truncate">
                                          {pageData?.programs?.[0]?.i20?.name ||
                                            "Current I-20"}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setAltProgramFileDeleted(true);
                                            if (
                                              generalForm?.current?.setFieldValue
                                            ) {
                                              generalForm.current.setFieldValue(
                                                "alt_program_file",
                                                null
                                              );
                                            }
                                          }}
                                          className="text-xs text-red-700 hover:text-red-900"
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex flex-col justify-center items-start relative w-full md:w-auto grow">
                                    <div className="px-2 text-xs flex flex-row justify-between items-center">
                                      Program Start Date
                                    </div>
                                    <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row justify-between items-center gap-2">
                                      <input
                                        type="date"
                                        className="bg-transparent w-full"
                                        value={altProgramStartDate}
                                        onChange={(e) =>
                                          setAltProgramStartDate(e.target.value)
                                        }
                                      />
                                    </div>
                                  </div>
                                  <div className="flex flex-col justify-center items-start relative w-full md:w-auto grow">
                                    <div className="px-2 text-xs flex flex-row justify-between items-center">
                                      Program End Date
                                    </div>
                                    <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row justify-between items-center gap-2">
                                      <input
                                        type="date"
                                        className="bg-transparent w-full"
                                        value={altProgramEndDate}
                                        onChange={(e) =>
                                          setAltProgramEndDate(e.target.value)
                                        }
                                      />
                                    </div>
                                  </div>
                                  <div className="flex flex-col justify-center items-start relative w-full md:w-auto grow">
                                    <div className="px-2 text-xs flex flex-row justify-between items-center">
                                      Comment
                                    </div>
                                    <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row justify-between items-center gap-2">
                                      <input
                                        type="text"
                                        className="bg-transparent w-full"
                                        value={altProgramMemo}
                                        onChange={(e) =>
                                          setAltProgramMemo(e.target.value)
                                        }
                                        placeholder="Why change?"
                                      />
                                    </div>
                                  </div>
                                </>
                              )}
                              <div className="flex flex-col justify-center items-start relative w-full md:w-auto">
                                <div className="px-2 text-xs flex flex-row justify-between items-center">
                                  &nbsp;
                                </div>
                                <div className="flex flex-row items-center gap-2">
                                  {!altProgramMode ? (
                                    <>
                                      {pageData?.programs?.[0] && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const program = pageData?.programs?.[0];
                                            setAltProgramMode("update");
                                            setAltProgramFileDeleted(false);
                                            setAltProgramMemo("");
                                            setAltProgramStartDate(
                                              program?.start_date
                                                ? format(
                                                    parseISO(program.start_date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")),
                                                    "yyyy-MM-dd"
                                                  )
                                                : ""
                                            );
                                            setAltProgramEndDate(
                                              program?.end_date
                                                ? format(
                                                    parseISO(program.end_date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")),
                                                    "yyyy-MM-dd"
                                                  )
                                                : ""
                                            );
                                          }}
                                          disabled={loading}
                                          className="px-4 py-2 text-xs font-bold border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white rounded-sm"
                                        >
                                          Update
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setAltProgramMode("add-new");
                                          setAltProgramFileDeleted(true);
                                          setAltProgramMemo("");
                                          setAltProgramStartDate("");
                                          setAltProgramEndDate("");
                                          if (generalForm?.current?.setFieldValue) {
                                            generalForm.current.setFieldValue(
                                              "alt_program_file",
                                              null
                                            );
                                          }
                                        }}
                                        disabled={loading}
                                        className="px-4 py-2 text-xs font-bold border border-gray-300 text-gray-700 transition-all hover:bg-gray-100 rounded-sm"
                                      >
                                        Add New
                                      </button>
                                    </>
                                  ) : (
                                    <>
                                      <button
                                        type="button"
                                        onClick={handleChangeProgram}
                                        disabled={loading}
                                        className="px-4 py-2 text-xs font-bold border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white rounded-sm"
                                      >
                                        Confirm
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setAltProgramMode(null);
                                          setAltProgramMemo("");
                                          setAltProgramFileDeleted(false);
                                          const program = pageData?.programs?.[0];
                                          if (program?.start_date) {
                                            setAltProgramStartDate(
                                              format(
                                                parseISO(program.start_date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")),
                                                "yyyy-MM-dd"
                                              )
                                            );
                                          } else {
                                            setAltProgramStartDate("");
                                          }
                                          if (program?.end_date) {
                                            setAltProgramEndDate(
                                              format(
                                                parseISO(program.end_date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")),
                                                "yyyy-MM-dd"
                                              )
                                            );
                                          } else {
                                            setAltProgramEndDate("");
                                          }
                                          if (generalForm?.current?.setFieldValue) {
                                            generalForm.current.setFieldValue(
                                              "alt_program_file",
                                              null
                                            );
                                          }
                                        }}
                                        disabled={loading}
                                        className="px-4 py-2 text-xs font-bold border border-gray-300 text-gray-700 transition-all hover:bg-gray-100 rounded-sm"
                                      >
                                        Cancel
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </InputLine>
                          )}
                          {!loadingProgramChangeLogs &&
                            programChangeLogs.length > 0 && (
                              <div className="w-full px-2 pb-4 mt-4">
                                <div className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
                                  History
                                </div>
                                <div className="w-full border rounded-md">
                                  <table className="w-full text-xs text-left">
                                    <thead className="bg-secondary">
                                      <tr>
                                        <th className="px-3 py-2">Old Start Date</th>
                                        <th className="px-3 py-2">New Start Date</th>
                                        <th className="px-3 py-2">Old End Date</th>
                                        <th className="px-3 py-2">New End Date</th>
                                        <th className="px-3 py-2">Old File</th>
                                        <th className="px-3 py-2">Type</th>
                                        <th className="px-3 py-2">Comment</th>
                                        <th className="px-3 py-2">Created At</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {programChangeLogs.map((row) => (
                                        <tr
                                          key={row.id}
                                          className="odd:bg-white even:bg-gray-50"
                                        >
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.old_start_date
                                              ? format(
                                                  parseISO(row.old_start_date),
                                                  "MM/dd/yyyy"
                                                )
                                              : "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.new_start_date
                                              ? format(
                                                  parseISO(row.new_start_date),
                                                  "MM/dd/yyyy"
                                                )
                                              : "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.old_end_date
                                              ? format(
                                                  parseISO(row.old_end_date),
                                                  "MM/dd/yyyy"
                                                )
                                              : "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.new_end_date
                                              ? format(
                                                  parseISO(row.new_end_date),
                                                  "MM/dd/yyyy"
                                                )
                                              : "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.old_file?.url ? (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  window.open(row.old_file.url)
                                                }
                                                className="text-mila_orange hover:text-gray-700"
                                                title="Download old file"
                                              >
                                                <Files size={14} />
                                              </button>
                                            ) : (
                                              "-"
                                            )}
                                          </td>
                                          <td className="px-3 py-2">
                                            {row.type || "-"}
                                          </td>
                                          <td className="px-3 py-2">
                                            {row.memo || "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.created_at
                                              ? format(
                                                  parseISO(row.created_at),
                                                  "MM/dd/yyyy HH:mm"
                                                )
                                              : "-"}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          {pageData?.programs?.length > 0 &&
                            pageData?.programs
                              .filter((p) => p.i20?.url)
                              .sort((a, b) =>
                                b.created_at > a.created_at ? 1 : -1
                              )
                              .map((program, index) => (
                                <InputLine
                                  key={index}
                                  title={index === 0 ? "Last I20 Files" : ""}
                                >
                                  <a
                                    type="button"
                                    href={program.i20.url}
                                    target="_blank"
                                    className="bg-primary text-white rounded-md p-1 px-2 h-8 mt-3 flex flex-row items-center justify-center text-xs gap-1"
                                  >
                                    <ExternalLink size={16} />
                                    Open File
                                  </a>
                                  <button
                                    type="button"
                                    disabled={sendingI20 === program.id}
                                    onClick={() => handleSendI20(program)}
                                    className="bg-primary text-white rounded-md p-1 px-2 h-8 mt-3 flex flex-row items-center justify-center text-xs gap-1 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                                  >
                                    {sendingI20 === program.id ? (
                                      <Loader2
                                        size={16}
                                        className="animate-spin"
                                      />
                                    ) : (
                                      <Send size={16} />
                                    )}
                                    Send I-20
                                  </button>
                                  <Input
                                    type="text"
                                    name="file_id"
                                    grow
                                    readOnly
                                    title="I20"
                                    defaultValue={program.i20.name}
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="date"
                                    name="start_date"
                                    grow
                                    readOnly
                                    title="Program Start Date"
                                    defaultValue={program.start_date}
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="date"
                                    name="end_date"
                                    grow
                                    readOnly
                                    title="Program End Date"
                                    defaultValue={program.end_date}
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                              ))}
                        </InputLineGroup>
                        <InputLineGroup
                          title="student-documents"
                          activeMenu={activeMenu === "student-documents"}
                        >
                          <InputLine title="Documents">
                            <SelectPopover
                              title="Filter"
                              name="filter"
                              grow
                              options={studentTypeOfDocuments}
                              defaultValue={studentTypeOfDocuments[0]}
                              setReturnPopover={setFilteredDocs}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          {(filteredDocs.value === "all_documents" ||
                            filteredDocs.value === "student_documents") && (
                            <InputLine title="Student Docs">
                              <div className="w-full flex flex-row items-start justify-between gap-6">
                                <div className="flex flex-row flex-wrap items-start justify-start gap-4 flex-1">
                                  {pageData.docs?.student_docs?.map((doc) => (
                                    <StudentDocument doc={doc} section="student_docs" />
                                  ))}
                                </div>
                                {openedPdf && openedPdfSection === "student_docs" && (
                                  <div className="flex flex-col border border-gray-200 bg-white rounded-md shadow-sm" style={{ width: 640 }}>
                                    <div className="flex flex-row items-center justify-between px-4 py-2 border-b border-gray-200 bg-slate-50 rounded-t-md">
                                      <span className="text-xs font-semibold text-slate-600">Document Preview</span>
                                      <button type="button" onClick={() => setOpenedPDF(null)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                                        <X size={14} />
                                      </button>
                                    </div>
                                    <div className="p-3 relative">
                                      <div style={{ overflow: "visible", height: "auto" }}>
                                        <PDFViewer download={true} file={{ url: openedPdf }} height={760} />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </InputLine>
                          )}
                          {(filteredDocs.value === "all_documents" ||
                            filteredDocs.value === "enrollment_documents") && (
                            <InputLine title="Enrollment Docs">
                              <div className="w-full flex flex-row items-start justify-between gap-6">
                                <div className="flex flex-row flex-wrap items-start justify-start gap-4 flex-1">
                                  {pageData.docs?.enrollment_docs?.map((doc) => (
                                    <StudentDocument doc={doc} section="enrollment_docs" />
                                  ))}
                                </div>
                                {openedPdf && openedPdfSection === "enrollment_docs" && (
                                  <div className="flex flex-col border border-gray-200 bg-white rounded-md shadow-sm" style={{ width: 640 }}>
                                    <div className="flex flex-row items-center justify-between px-4 py-2 border-b border-gray-200 bg-slate-50 rounded-t-md">
                                      <span className="text-xs font-semibold text-slate-600">Document Preview</span>
                                      <button type="button" onClick={() => setOpenedPDF(null)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                                        <X size={14} />
                                      </button>
                                    </div>
                                    <div className="p-3 relative">
                                      <div style={{ overflow: "visible", height: "auto" }}>
                                        <PDFViewer download={true} file={{ url: openedPdf }} height={760} />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </InputLine>
                          )}
                          {(filteredDocs.value === "all_documents" ||
                            filteredDocs.value === "financial_documents") && (
                            <InputLine title="Financial Docs">
                              <div className="w-full flex flex-row items-start justify-between gap-6">
                                <div className="flex flex-row flex-wrap items-start justify-start gap-4 flex-1">
                                  {pageData.docs?.financial_docs?.map((doc) => (
                                    <StudentDocument doc={doc} section="financial_docs" />
                                  ))}
                                </div>
                                {openedPdf && openedPdfSection === "financial_docs" && (
                                  <div className="flex flex-col border border-gray-200 bg-white rounded-md shadow-sm" style={{ width: 640 }}>
                                    <div className="flex flex-row items-center justify-between px-4 py-2 border-b border-gray-200 bg-slate-50 rounded-t-md">
                                      <span className="text-xs font-semibold text-slate-600">Document Preview</span>
                                      <button type="button" onClick={() => setOpenedPDF(null)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                                        <X size={14} />
                                      </button>
                                    </div>
                                    <div className="p-3 relative">
                                      <div style={{ overflow: "visible", height: "auto" }}>
                                        <PDFViewer download={true} file={{ url: openedPdf }} height={760} />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </InputLine>
                          )}
                          {(filteredDocs.value === "all_documents" ||
                            filteredDocs.value === "other_documents") && (
                            <InputLine title="Other Docs">
                              <div className="w-full flex flex-row items-start justify-between gap-6">
                                <div className="flex flex-row flex-wrap items-start justify-start gap-4 flex-1">
                                  {pageData.docs?.other_docs?.map((doc) => (
                                    <StudentDocument doc={doc} deletable={true} section="other_docs" />
                                  ))}
                                </div>
                                {openedPdf && openedPdfSection === "other_docs" && (
                                  <div className="flex flex-col border border-gray-200 bg-white rounded-md shadow-sm" style={{ width: 640 }}>
                                    <div className="flex flex-row items-center justify-between px-4 py-2 border-b border-gray-200 bg-slate-50 rounded-t-md">
                                      <span className="text-xs font-semibold text-slate-600">Document Preview</span>
                                      <button type="button" onClick={() => setOpenedPDF(null)} className="text-slate-400 hover:text-slate-700 cursor-pointer">
                                        <X size={14} />
                                      </button>
                                    </div>
                                    <div className="p-3 relative">
                                      <div style={{ overflow: "visible", height: "auto" }}>
                                        <PDFViewer download={true} file={{ url: openedPdf }} height={760} />
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </InputLine>
                          )}

                          {pageData.defaultOtherDocs?.map((doc, index) => {
                            return (
                              <Scope key={index} path={`other_docs[${index}]`}>
                                <InputLine
                                  title={index === 0 ? "Add files..." : ""}
                                >
                                  <Input
                                    type="hidden"
                                    name="document_id"
                                    defaultValue={doc.id}
                                    InputContext={InputContext}
                                  />
                                  {doc.multiple ? (
                                    <FileInputMultiple
                                      key={doc.id}
                                      name="file_id"
                                      title={doc.short_name}
                                      grow
                                      InputContext={InputContext}
                                    />
                                  ) : (
                                    <FileInput
                                      key={doc.id}
                                      name="file_id"
                                      title={doc.short_name}
                                      grow
                                      InputContext={InputContext}
                                    />
                                  )}
                                </InputLine>
                              </Scope>
                            );
                          })}
                        </InputLineGroup>
                        <InputLineGroup
                          title="Comments"
                          activeMenu={activeMenu === "comments"}
                        >
                          <Comments enrollment_id={pageData?.enrollments?.[0]?.id} />
                        </InputLineGroup>

                        <InputLineGroup
                          title="student-history"
                          activeMenu={activeMenu === "student-history"}
                        >
                          <div className="w-full p-4">
                            {loadingStudentHistory ? (
                              <div className="flex items-center justify-center py-8">
                                <Loader2 size={20} className="animate-spin text-gray-400" />
                              </div>
                            ) : studentHistory.length === 0 ? (
                              <p className="text-center text-gray-400 py-8">No history found.</p>
                            ) : (
                              <>
                                <div className="flex flex-row flex-wrap items-end justify-end gap-3 mb-3">
                                  <label className="flex flex-col gap-1 text-xs text-gray-600">
                                    From
                                    <input
                                      type="date"
                                      value={historyEventDateFrom}
                                      max={historyEventDateTo || undefined}
                                      onChange={(event) => setHistoryEventDateFrom(event.target.value)}
                                      className="h-8 min-w-40 rounded-sm border border-gray-300 bg-white px-2 text-xs text-gray-700"
                                    />
                                  </label>
                                  <label className="flex flex-col gap-1 text-xs text-gray-600">
                                    To
                                    <input
                                      type="date"
                                      value={historyEventDateTo}
                                      min={historyEventDateFrom || undefined}
                                      onChange={(event) => setHistoryEventDateTo(event.target.value)}
                                      className="h-8 min-w-40 rounded-sm border border-gray-300 bg-white px-2 text-xs text-gray-700"
                                    />
                                  </label>
                                  <label className="flex flex-col gap-1 text-xs text-gray-600">
                                    Action
                                    <select
                                      value={historyActionFilter}
                                      onChange={(event) => setHistoryActionFilter(event.target.value)}
                                      className="h-8 min-w-48 rounded-sm border border-gray-300 bg-white px-2 text-xs text-gray-700"
                                    >
                                      <option value="">All actions</option>
                                      {studentHistoryActions.map((action) => (
                                        <option key={action} value={action}>
                                          {action}
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                  {(historyEventDateFrom || historyEventDateTo || historyActionFilter) && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setHistoryEventDateFrom("");
                                        setHistoryEventDateTo("");
                                        setHistoryActionFilter("");
                                      }}
                                      className="h-8 rounded-sm border border-gray-300 bg-white px-3 text-xs font-semibold text-gray-600 hover:bg-gray-50"
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>

                                {filteredStudentHistory.length === 0 ? (
                                  <p className="text-center text-gray-400 py-8">
                                    No history found for the selected filters.
                                  </p>
                                ) : (
                                <table className="w-full text-xs border-collapse">
                                <thead>
                                  <tr className="bg-gray-100 text-left">
                                    <th className="px-3 py-2 font-semibold border-b">Date</th>
                                    <th className="px-3 py-2 font-semibold border-b">Action</th>
                                    <th className="px-3 py-2 font-semibold border-b">Group (Old → New)</th>
                                    <th className="px-3 py-2 font-semibold border-b">Expected Start Date (Old → New)</th>
                                    <th className="px-3 py-2 font-semibold border-b">Memo</th>
                                    <th className="px-3 py-2 font-semibold border-b">Deleted</th>
                                    <th className="px-3 py-2 font-semibold border-b">Changed By</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredStudentHistory.map((row) => (
                                    <tr key={row.id} className="border-b hover:bg-gray-50">
                                      <td className="px-3 py-2 whitespace-nowrap">
                                        {formatDateForTable(row.created_at) || "—"}
                                      </td>
                                      <td className="px-3 py-2">
                                        <span className={`font-semibold ${
                                          row.action === "ACTIVATED" ? "text-emerald-600" :
                                          row.action === "INACTIVATED" ? "text-red-500" :
                                          row.action === "TRANSFERRED" ? "text-blue-500" :
                                          row.action === "ENROLLED" ? "text-purple-600" :
                                          row.action === "EXPECTED_DATE_CHANGED" ? "text-orange-500" :
                                          row.action === "VACATION" ? "text-sky-600" :
                                          row.action === "MEDICAL EXCUSE" ? "text-rose-600" :
                                          "text-gray-600"
                                        }`}>
                                          {row.action || "—"}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap">
                                        {row.is_legacy
                                          ? (row.old_legacy_group || row.new_legacy_group
                                              ? <>{formatLegacyGroup(row.old_legacy_group)} → {formatLegacyGroup(row.new_legacy_group)}</>
                                              : formatLegacyGroup(row.legacy_group))
                                          : row.action === "TRANSFERRED"
                                            ? <>{row.old_student_group?.name || "—"} → {row.new_student_group?.name || "—"}</>
                                            : row.new_student_group?.name
                                              ? row.new_student_group.name
                                              : "—"
                                        }
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap">
                                        {row.is_leave_event
                                          ? <>
                                              {formatDateForTable(row.period_start) || "—"}
                                              {" → "}
                                              {formatDateForTable(row.period_end) || "—"}
                                            </>
                                          : row.is_legacy
                                          ? <>
                                              {formatDateForTable(row.start_date) || "—"}
                                              {" → "}
                                              {formatDateForTable(row.end_date) || "—"}
                                            </>
                                          : row.action === "EXPECTED_DATE_CHANGED"
                                            ? <>
                                                {formatDateForTable(row.old_expected_start_date) || "—"}
                                                {" → "}
                                                {formatDateForTable(row.new_expected_start_date) || "—"}
                                              </>
                                            : row.new_expected_start_date
                                              ? formatDateForTable(row.new_expected_start_date)
                                              : "—"
                                        }
                                      </td>
                                      <td className="px-3 py-2">
                                        {row.is_legacy
                                          ? [row.entry_reason, row.exit_reason].filter(Boolean).join(" / ") || "—"
                                          : row.memo || "—"}
                                      </td>
                                      <td className="px-3 py-2">
                                        <span className={row.is_deleted ? "font-semibold text-red-600" : "text-gray-500"}>
                                          {row.is_deleted ? "Yes" : "No"}
                                        </span>
                                      </td>
                                      <td className="px-3 py-2">
                                        {row.is_legacy || row.is_migrated
                                          ? "Migration Process"
                                          : row.created_by_user?.name || "—"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                                )}
                              </>
                            )}
                          </div>
                        </InputLineGroup>
                      </>
                    ) : (
                      <FormLoading />
                    )}
                  </Form>
                </div>
              </InputContext.Provider>
            </div>
          </div>
        )
      ) : null}
    </Preview>
  );
}
